// AARP membership offer on the reverse thank-you page (Paul, 2026-10-16).
//
// RateZip holds an Impact affiliate link for AARP membership, brokered by
// Strand Marketing (RateZip Impact publisher 81868, $35 per membership).
// The offer is shown to reverse applicants AFTER the lead is delivered,
// below the matched lender's own block, and never to an applicant whose
// lead was bought by FAR (Finance of America Reverse): FAR is an exclusive
// buyer under restricted follow-up terms, so a FAR-matched applicant must
// see nothing beyond the neutral forwarded page.
//
// Scope is the thank-you page only. Email and SMS promotion are out of
// scope (email hold since 2026-09-18; SMS consent covers mortgage lenders
// only).

export const AARP_OFFER_URL = "https://aarp.pxf.io/jR3rGM";

// The one buyer that never sees the offer. Compared case-insensitively on
// the trimmed boberdoo buyer string, so "FAR", "far" and " FAR " all match.
export const AARP_EXCLUDED_BUYER = "FAR";

// Impact Sub IDs (partner-only reporting dimensions). Impact's help center
// ("Sub ID & Shared ID Parameters Explained for Partners") states that all
// tracking links including vanity links accept subId1..3 appended as query
// parameters, each alphanumeric, at most 255 characters. Values here are
// therefore letters and digits only: subId1 names the placement, subId2 the
// buyer the lead went to ("unmatched" when no buyer was named).
export const AARP_SUBID_PLACEMENT = "reversethankyou";
export const AARP_SUBID_UNMATCHED = "unmatched";

// COPY, written to Strand's "AARP 51/49 Copy Guidance" (last updated
// 3/2024, received from Chelsea Chapman at Strand on 2026-10-09):
//   - lead with social impact; keep at least 51% of the words on AARP's
//     social mission and at most 49% on commercial discounts;
//   - say "people age 50+", never "seniors", "elderly" or "old";
//   - name no insurance or travel providers;
//   - "All copy must be submitted and approved by your Strand contact
//     prior to going live." Chelsea has NOT approved this copy yet.
// The body below runs social mission first (Social Security, Medicare,
// prescription drug costs, fraud prevention, brain health, caregiving), then
// one shorter commercial sentence (prescriptions, groceries, dining, travel,
// AARP The Magazine). The price point ($15 first year, 25% off) is carried
// only by AARP's own banner image, so this text never goes stale on price.
//
// PLACEMENT WARNING (Strand's "AARP Media Restrictions", same email):
// "Reverse mortgages" is on AARP's list of excluded properties/media. This
// block sits on a reverse mortgage thank-you page. Do not take this live
// until Chelsea confirms in writing that the placement is permitted.
export const AARP_OFFER_HEADLINE = "Not an AARP member yet?";
export const AARP_OFFER_BODY =
  "AARP advocates for people age 50+ on Social Security, Medicare and prescription drug costs, " +
  "and offers free resources on fraud prevention, brain health and caregiving. " +
  "Members also save on prescriptions, groceries, dining and travel, and receive AARP The Magazine. " +
  "Membership is optional and separate from your mortgage request.";
export const AARP_OFFER_CTA = "Learn about AARP membership";

// Alt text for AARP's own banner (Strand "Refresh Banners_AARP",
// AARP_Lifestyle_700x400.jpg). It repeats the words printed on the image.
export const AARP_OFFER_BANNER_ALT =
  "AARP. Enjoy membership for 25% off. Only $15 for your first year with automatic renewal. Join or renew now.";

// Affiliate disclosure. Required wording.
export const AARP_OFFER_DISCLOSURE =
  "RateZip may earn a commission if you join AARP through this link.";

/**
 * Whether the AARP offer may be shown for the buyer that bought the lead.
 * True for every buyer except FAR, and true when no buyer was named
 * (null, undefined, empty string). Pure: no DOM, no context.
 */
export function showAarpOffer(buyer) {
  if (buyer === null || buyer === undefined) return true;
  return String(buyer).trim().toUpperCase() !== AARP_EXCLUDED_BUYER;
}

/**
 * Impact-safe Sub ID value for a buyer string: lower-cased, letters and
 * digits only, "unmatched" when nothing is left (or no buyer was named).
 */
export function aarpBuyerSubId(buyer) {
  if (buyer === null || buyer === undefined) return AARP_SUBID_UNMATCHED;
  const slug = String(buyer).toLowerCase().replace(/[^a-z0-9]/g, "");
  return slug || AARP_SUBID_UNMATCHED;
}

/**
 * The tracking URL for the offer link with placement and buyer attribution.
 */
export function buildAarpOfferUrl(buyer) {
  const url = new URL(AARP_OFFER_URL);
  url.searchParams.set("subId1", AARP_SUBID_PLACEMENT);
  url.searchParams.set("subId2", aarpBuyerSubId(buyer));
  return url.toString();
}
